import { createReducer, createAction } from '@reduxjs/toolkit';
import ReactNativeBlobUtil from 'react-native-blob-util';

import { uploadImage } from '@reducers/imageHandling';
import { isWeb } from '@helpers';
import type { RootState, AppDispatch } from '@store/types';
import type { UploadQueueItem, UploadQueueState } from './types';

// Re-export types for consumers
export type { UploadQueueItem, UploadQueueItemStatus, UploadQueueState } from './types';

const initState: UploadQueueState = {
  uploadQueue: {},
};

export const updateUploadQueue = createAction<UploadQueueItem>('imageHandling/updateUploadQueue');
export const deleteFromUploadQueue = createAction<UploadQueueItem>('imageHandling/deleteFromUploadQueue');
export const clearQueue = createAction('imageHandling/clearQueue');
export const terminateUploading = createAction('imageHandling/terminateUploading');
export const clear = createAction('clear');

export const initListImages = () => async (dispatch: AppDispatch, getState: () => RootState) => {
  try {
    const {
      uploadQueue: { uploadQueue },
    } = getState();
    for (const index in uploadQueue) {
      const item = uploadQueue[index];
      if (item.img.base64 && (item.status === 'completed' || item.status === 'error')) {
        dispatch(deleteFromUploadQueue(item));
        continue;
      }
      if (!item.img.base64 || item.status === 'completed') {
        continue;
      }
      if (isWeb) {
        const res = await fetch(item.img.base64 as string, { ignoreInterceptor: 'true' } as RequestInit);
        const blob = await res.blob();
        const file = new File([blob], item.img.photo.fileName, { type: 'image/png' });
        dispatch(
          updateUploadQueue({
            ...item,
            img: {
              ...item.img,
              photo: {
                ...item.img.photo,
                file: file,
                uri: window.URL.createObjectURL(blob),
              },
            },
          })
        );
      } else {
        const base64data = item.img.base64 as string;
        const dirs = ReactNativeBlobUtil.fs.dirs.CacheDir;
        const path = dirs + `/${item.name.replace('.', '')}.png`;
        await ReactNativeBlobUtil.fs.writeFile(path, base64data, 'base64');
        const file = `file://${path}`;
        dispatch(
          updateUploadQueue({
            ...item,
            img: {
              ...item.img,
              photo: {
                ...item.img.photo,
                uri: file,
              },
            },
          })
        );
      }
    }
    await dispatch(startQueue());
  } catch (e) {
    console.log('Error in update image list', e);
  }
};

export const startQueue = () => async (dispatch: AppDispatch, getState: () => RootState) => {
  try {
    const {
      uploadQueue: { uploadQueue },
    } = getState();

    for (const index in uploadQueue) {
      const item = uploadQueue[index];
      try {
        if (item.status !== 'pending') {
          continue;
        }
        await dispatch(
          updateUploadQueue({
            ...item,
            status: 'uploading',
          })
        );

        const image = item.img;

        const result = await dispatch(
          uploadImage({
            ...image,
            name: item.name,
          })
        );

        let imgData: UploadQueueItem;
        if (result && result.status === 200) {
          imgData = {
            ...item,
            img: {
              ...item.img,
              base64: false,
            },
            status: 'completed',
          };
        } else {
          imgData = {
            ...item,
            wasError: true,
            img: {
              ...item.img,
            },
          };
          if (item.wasError) {
            imgData.status = 'error';
          }
        }
        await dispatch(updateUploadQueue(imgData));
      } catch (e) {
        let imgData: UploadQueueItem = {
          ...item,
          wasError: true,
          img: {
            ...item.img,
          },
        };
        if (item.wasError) {
          imgData.status = 'error';
        }
        await dispatch(updateUploadQueue(imgData));
      }
    }
  } catch (e) {
    console.log(e, 'e');
  }
};

export default createReducer<UploadQueueState>(initState, {
  [updateUploadQueue.type]: (state, action: ReturnType<typeof updateUploadQueue>) => ({
    ...state,
    uploadQueue: {
      ...state?.uploadQueue,
      [action.payload.id]: action.payload,
    },
  }),
  [deleteFromUploadQueue.type]: (state, action: ReturnType<typeof deleteFromUploadQueue>) => {
    const newQueue = Object.assign({}, state?.uploadQueue);
    delete newQueue[action.payload.id];
    return {
      ...state,
      uploadQueue: newQueue,
    };
  },
  [clearQueue.type]: (state) => ({
    ...state,
    uploadQueue: {},
  }),
  [terminateUploading.type]: (state) => {
    const newList: Record<string, UploadQueueItem> = {};
    Object.values(state.uploadQueue).forEach((i) => {
      if (i.status === 'completed') {
        newList[i.id] = i;
      } else {
        newList[i.id] = {
          ...i,
          status: 'error',
        };
      }
    });
    return {
      ...state,
      uploadQueue: newList,
    };
  },
  [clear.type]: () => {
    return initState;
  },
});
